"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useMemo, type ReactNode } from "react";

import { Button } from "@/components/Button";
import { Link } from "@/i18n/navigation";
import { useMarketingAnalytics } from "@/hooks/useMarketingAnalytics";
import { appendAttributionToUrl } from "@/lib/analytics/append-attribution-to-url";
import { getActiveWebsiteExperimentProperties } from "@/lib/analytics/experiments";
import { resolveMarketingAttributionParams } from "@/lib/analytics/utm-params";

import type { ButtonLinkProps } from "./ButtonLink.types";

function getDestinationType(href: string): string {
  if (href.includes("/signup")) {
    return "signup";
  }
  if (href.includes("/login")) {
    return "login";
  }
  if (
    href.includes("/contact") ||
    href.includes("intent=enterprise") ||
    href.includes("intent=security")
  ) {
    return "contact";
  }
  if (href.includes("/pricing")) {
    return "pricing";
  }
  if (href.startsWith("http") || href.startsWith("mailto:")) {
    return "external";
  }
  return "internal";
}

function getTrackingLabel(children: ReactNode, trackingLabel?: string): string {
  if (trackingLabel) {
    return trackingLabel;
  }
  return typeof children === "string" ? children : "";
}

export const ButtonLink = ({
  href,
  children,
  variant = "default",
  size = "default",
  className,
  trackingLocation,
  trackingLabel,
}: ButtonLinkProps) => {
  const searchParams = useSearchParams();
  const { track } = useMarketingAnalytics();

  const attribution = useMemo(
    () => resolveMarketingAttributionParams(searchParams),
    [searchParams],
  );

  const resolvedHref = useMemo(
    () => appendAttributionToUrl(href, attribution),
    [href, attribution],
  );

  const handleClick = useCallback(() => {
    if (!trackingLocation) {
      return;
    }

    track({
      name: "marketing_cta_clicked",
      properties: {
        location: trackingLocation,
        label: getTrackingLabel(children, trackingLabel),
        href: resolvedHref,
        destination_type: getDestinationType(href),
        ...attribution,
        ...getActiveWebsiteExperimentProperties(),
      },
    });
  }, [
    attribution,
    children,
    href,
    resolvedHref,
    track,
    trackingLabel,
    trackingLocation,
  ]);

  const isExternal = href.startsWith("http") || href.startsWith("mailto:");

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      render={
        isExternal ? (
          <a href={resolvedHref} onClick={handleClick} />
        ) : (
          <Link href={resolvedHref} onClick={handleClick} />
        )
      }
    >
      {children}
    </Button>
  );
};
